/**
 * The Sites list (plan 7.10, the user's `sites-list.png`): shared, platform
 * free logic for the web table and the native list. Rows come from
 * `GET /api/sites` (the server filters by `q` too); the list also filters the
 * rows it already holds as the user types, so a keystroke never waits for
 * the network.
 */
import type { PublishedSiteSummary, SiteListTab } from '@tabbio/types';
import { siteHost } from './publish-sheet-model';

export const SITES_LIST_TABS: readonly SiteListTab[] = ['owned', 'shared'];

export const SITES_SEARCH_DEBOUNCE_MS = 250;

export function normalizeSitesTab(value: unknown): SiteListTab {
  const raw = Array.isArray(value) ? value[0] : value;
  return raw === 'shared' ? 'shared' : 'owned';
}

export function sitesTabLabelKey(tab: SiteListTab): string {
  return tab === 'shared' ? 'sites.tabs.shared' : 'sites.tabs.owned';
}

/**
 * Filters by title and address, case-insensitive, as the user types (the
 * same rule the server applies to `q`). Blank matches everything.
 */
export function filterSites<T extends Pick<PublishedSiteSummary, 'title' | 'name'>>(
  sites: readonly T[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...sites];
  return sites.filter(
    (site) =>
      site.title.toLowerCase().includes(needle) ||
      (site.name ?? '').toLowerCase().includes(needle),
  );
}

/** "Everyone" for a live site, "Only you" for a draft, unpublished or suspended one. */
export type SiteAudience = 'everyone' | 'onlyYou';

export function siteAudience(site: Pick<PublishedSiteSummary, 'status' | 'name'>): SiteAudience {
  return site.status === 'live' && site.name ? 'everyone' : 'onlyYou';
}

/**
 * The short age of a row, as the reference shows it: "2h", "3d", "1w" (and
 * "now" under a minute, minutes under an hour, weeks past a week). Units are
 * copy keys so Arabic reads its own.
 */
export function siteAge(
  iso: string | null | undefined,
  now: number = Date.now(),
): { key: string; count: number } | null {
  if (!iso) return null;
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return null;
  const seconds = Math.max(0, Math.floor((now - time) / 1000));
  if (seconds < 60) return { key: 'sites.age.now', count: 0 };
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return { key: 'sites.age.minutes', count: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { key: 'sites.age.hours', count: hours };
  const days = Math.floor(hours / 24);
  if (days < 7) return { key: 'sites.age.days', count: days };
  return { key: 'sites.age.weeks', count: Math.floor(days / 7) };
}

export type SiteRowModel = {
  id: string;
  artifactId: string;
  threadId: string | null;
  title: string;
  /** `<name>.tabbio.site`, or null for a draft without an address. */
  host: string | null;
  url: string | null;
  audience: SiteAudience;
  age: { key: string; count: number } | null;
  previewUrl: string | null;
  status: PublishedSiteSummary['status'];
  live: boolean;
};

/** The age counts from the last change, like the reference's "1w". */
export function siteRowModel(site: PublishedSiteSummary, now: number = Date.now()): SiteRowModel {
  const host = site.url ? siteHost(site.url) : null;
  return {
    id: site.id,
    artifactId: site.artifactId,
    threadId: site.threadId,
    title: site.title,
    host,
    url: site.url,
    audience: siteAudience(site),
    age: siteAge(site.updatedAt ?? site.publishedAt, now),
    previewUrl: site.previewUrl,
    status: site.status,
    live: site.status === 'live' && Boolean(site.name),
  };
}

/** The row menu, exactly as `sites-list-menu.png`: Share, Edit, Analytics, Settings. */
export const SITE_ROW_MENU = ['share', 'edit', 'analytics', 'settings'] as const;
export type SiteRowMenuAction = (typeof SITE_ROW_MENU)[number];

/** The settings route of a site, with its tab. */
export function siteSettingsHref(siteId: string, tab: 'settings' | 'analytics' = 'settings') {
  const path = `/sites/${encodeURIComponent(siteId)}`;
  return tab === 'analytics' ? `${path}?tab=analytics` : path;
}

/** What the list shows under the header: rows, the empty state, or a notice. */
export function sitesListState(input: {
  loading: boolean;
  error: boolean;
  rows: number;
  query: string;
  tab: SiteListTab;
}): 'loading' | 'error' | 'rows' | 'empty' | 'noMatches' | 'sharedEmpty' {
  if (input.loading) return 'loading';
  if (input.error) return 'error';
  if (input.rows > 0) return 'rows';
  if (input.tab === 'shared') return 'sharedEmpty';
  return input.query.trim() ? 'noMatches' : 'empty';
}
